// Bands product gallery: show only the selected metal tone's images.
// Media are tagged server-side with data-media-tone (yellow/white/rose) derived
// from their alt text. Platinum reuses the white-gold imagery. If a product has
// no tone-tagged media (or no metal selected), every image is shown so the
// gallery never ends up empty.
(function () {
  'use strict';

  function toneFromText(text) {
    if (!text) return '';
    const t = text.toLowerCase();
    if (t.indexOf('rose') !== -1) return 'rose';
    if (t.indexOf('white') !== -1) return 'white';
    if (t.indexOf('yellow') !== -1) return 'yellow';
    if (t.indexOf('platinum') !== -1) return 'white';
    return '';
  }

  function getSelectedTone() {
    const root = document.querySelector('product-variant-selects') || document;

    const checked = root.querySelectorAll('input[type="radio"]:checked');
    for (const input of checked) {
      const tone = toneFromText(input.value);
      if (tone) return tone;
    }

    const selects = root.querySelectorAll('select');
    for (const select of selects) {
      const tone = toneFromText(select.value);
      if (tone) return tone;
    }

    return '';
  }

  // Swiper does NOT support hiding slides with display:none -- it skips such
  // slides when building its layout grid, so the grid (and the thumbs index
  // map) end up shorter than the DOM slide list. Navigation then uses a 0..N
  // DOM index against the shorter grid and overshoots: slideTo and thumbnail
  // clicks jump to the last slide, and the gallery<->thumbs coupling desyncs.
  // To keep Swiper consistent we physically keep ONLY the matching slides as
  // wrapper children (caching the full set so we can re-filter on each change).
  function filterSwiper(swiperEl, shouldShow) {
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    // Capture the pristine, complete slide set ONCE -- on the first pass, before
    // product-variants.js re-renders the gallery at init. We keep these element
    // references as the source of truth and re-filter from them every time.
    if (!swiperEl._bandsAllSlides) {
      swiperEl._bandsAllSlides = Array.from(wrapper.querySelectorAll(':scope > .swiper-slide'));
    }
    const desired = swiperEl._bandsAllSlides.filter((slide) => shouldShow(slide));
    const current = Array.from(wrapper.querySelectorAll(':scope > .swiper-slide'));

    // Idempotent: if the wrapper already holds exactly the desired slides, do
    // nothing. The filter fires several times per change (debounce schedule x
    // multiple events); without this guard each pass would needlessly rebuild
    // the DOM and re-run Swiper.update().
    const unchanged =
      current.length === desired.length && current.every((el, i) => el === desired[i]);
    if (unchanged) return;

    // Remove EVERY current slide -- including any re-rendered by another script
    // (product-variants.js rebuilds the initial variant's slides at load, and
    // those fresh nodes are NOT in our cache, so a reference-only detach would
    // miss them and leave stale slides behind). Then re-append only the matching
    // slides, in order, from the pristine cache.
    current.forEach((slide) => wrapper.removeChild(slide));
    desired.forEach((slide) => {
      slide.classList.remove('hidden');
      wrapper.appendChild(slide);
    });

    const inst = swiperEl.instance;
    if (!inst || typeof inst.update !== 'function') return;
    inst.update();
    if (typeof inst.slideTo === 'function') inst.slideTo(0, 0);
  }

  function applyFilter(gallery) {
    const items = gallery.querySelectorAll('[data-media-tone]');
    if (!items.length) return;

    const tone = getSelectedTone();

    let matches = 0;
    items.forEach((item) => {
      if (item.getAttribute('data-media-tone') === tone) matches += 1;
    });

    // Fallback: nothing matches (untagged product) or no tone selected -> show all.
    const showAll = tone === '' || matches === 0;
    const shouldShow = (el) => showAll || el.getAttribute('data-media-tone') === tone;

    // Swipers (mobile slider + thumbs): physically include only matching slides.
    gallery.querySelectorAll('swiper-product-gallery, swiper-product-thumbs').forEach((swiperEl) => {
      filterSwiper(swiperEl, shouldShow);
    });

    // Re-sync the gallery once more after BOTH swipers are filtered so the
    // active-thumb highlight maps to the first slide of the new tone.
    const galleryEl = gallery.querySelector('swiper-product-gallery');
    if (galleryEl && galleryEl.instance && typeof galleryEl.instance.slideTo === 'function') {
      galleryEl.instance.slideTo(0, 0);
    }

    // Desktop grid (not a Swiper): a plain CSS hide is correct here.
    gallery.querySelectorAll('.main-product__media--grid-item').forEach((gridItem) => {
      const toned = gridItem.querySelector('[data-media-tone]');
      gridItem.classList.toggle('hidden', !(toned ? shouldShow(toned) : true));
    });
  }

  function init() {
    const SELECTOR = '[data-filter-variant-tone="true"]';
    if (!document.querySelector(SELECTOR)) return;

    // Re-query every run. The theme rebuilds the product media gallery on a
    // variant change (an async section fetch), so a list captured once would
    // point at detached nodes and leave the freshly-rendered gallery unfiltered
    // -- which is why the previous tone's images "stick". Always filter whatever
    // gallery is in the DOM right now.
    const run = () => document.querySelectorAll(SELECTOR).forEach(applyFilter);

    // Because the rebuild is async, a single post-change pass can fire before the
    // new markup lands. Re-apply across a short window so we catch the gallery
    // once it's swapped in. getSelectedTone() always reads the *current*
    // selection, so a late pass can never apply a stale tone.
    const scheduleRun = () => [0, 120, 300, 700, 1400].forEach((d) => setTimeout(run, d));

    run();

    document.addEventListener('change', (event) => {
      if (event.target && event.target.closest('product-variant-selects')) {
        scheduleRun();
      }
    });

    document.addEventListener('variant:change', scheduleRun);

    if (typeof subscribe === 'function') {
      const eventName =
        typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange
          ? PUB_SUB_EVENTS.variantChange
          : 'variant-change';
      try {
        subscribe(eventName, scheduleRun);
      } catch (e) {
        /* no-op */
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
